import React from 'react';
import { UsageStats } from './types';

interface TokenTrackerProps {
  stats: UsageStats;
  lastRunStats: UsageStats | null;
}

export const TokenTracker: React.FC<TokenTrackerProps> = ({ stats, lastRunStats }) => {
  // Pricing Constants (Estimated based on Gemini Flash pricing)
  // Prices are per 1 Million Tokens
  const PRICE_INPUT_1M = 0.075; 
  const PRICE_OUTPUT_1M = 0.30; 
  // Veo pricing is hypothetical/preview
  const PRICE_PER_VIDEO = 1.50; 

  const calculateCost = (s: UsageStats) => {
    const inputCost = (s.promptTokenCount / 1_000_000) * PRICE_INPUT_1M;
    const outputCost = (s.candidatesTokenCount / 1_000_000) * PRICE_OUTPUT_1M;
    const videoCost = s.videosGenerated * PRICE_PER_VIDEO;
    
    return (inputCost + outputCost + videoCost).toFixed(4);
  };

  if (stats.totalTokenCount === 0 && stats.videosGenerated === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 bg-slate-900/95 border border-slate-700 p-4 rounded-xl backdrop-blur-md shadow-2xl text-xs font-mono max-w-[280px] animate-in slide-in-from-left-5">
      <div className="flex items-center justify-between mb-3 border-b border-slate-700 pb-2">
        <span className="text-emerald-400 font-bold uppercase tracking-wider flex items-center gap-2">
           <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
           Cost Tracker
        </span>
        <span className="text-slate-500 text-[10px]">(Internal)</span>
      </div>
      
      {/* Last Run Section */}
      {lastRunStats && (
        <div className="mb-3 pb-3 border-b border-slate-700/50">
             <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 font-bold">Last Request</div>
             <div className="flex justify-between text-slate-300">
                <span>Tokens:</span>
                <span>{(lastRunStats.totalTokenCount).toLocaleString()}</span>
             </div>
             <div className="flex justify-between items-center mt-1">
                <span className="text-slate-400">Cost:</span>
                <span className="text-sm font-bold text-white">${calculateCost(lastRunStats)}</span>
             </div>
        </div>
      )}
      
      {/* Session Total Section */}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 font-bold">Session Total</div>
        <div className="space-y-1 text-slate-300">
            <div className="flex justify-between">
            <span>In/Out Tokens:</span>
            <span className="text-slate-400">{stats.promptTokenCount.toLocaleString()} / {stats.candidatesTokenCount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
            <span>Videos:</span>
            <span className="text-slate-400">{stats.videosGenerated}</span>
            </div>
            
            <div className="mt-2 pt-2 border-t border-slate-700/50 flex justify-between items-center bg-slate-800/50 p-2 rounded-lg">
            <span className="font-bold text-slate-300">Total:</span>
            <span className="text-lg font-bold text-emerald-400">${calculateCost(stats)}</span>
            </div>
        </div>
      </div>

      <div className="mt-2 text-[9px] text-slate-600 leading-tight">
        ${PRICE_INPUT_1M}/1M in • ${PRICE_OUTPUT_1M}/1M out • ${PRICE_PER_VIDEO}/video
      </div>
    </div>
  );
};


